import { MoreHorizontal, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  listMembers,
  removeMember,
  resendInvite,
  updateMemberRole,
} from "../../api/members";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { Project, ProjectMember } from "../../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { LeaveProjectDialog } from "./LeaveProjectDialog";

interface MembersTabProps {
  project: Project;
}

// Avatar initials — first letter of each word, up to 2 characters.
function initials(name?: string, email?: string): string {
  const src = name || email || "?";
  return src
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function roleChangeContent(member: ProjectMember, newRole: string) {
  const name = member.name || member.email;
  const roleName = newRole.charAt(0).toUpperCase() + newRole.slice(1);

  if (newRole === "owner") {
    return {
      title: "Promote to Owner?",
      body: `${name} will become an Owner of this project. Owners can delete the project, remove members, and change any member's role — including yours.`,
      action: "Make Owner",
    };
  }
  if (member.isCurrentUser) {
    return {
      title: `Step down to ${roleName}?`,
      body:
        newRole === "member"
          ? "You will lose the ability to manage members and project settings."
          : "You will only be able to view project resources and won't be able to deploy services or make changes.",
      action: "Step down",
    };
  }
  return {
    title: `Change role to ${roleName}?`,
    body:
      newRole === "member"
        ? `${name} will be able to deploy services but cannot manage project settings or members.`
        : `${name} will only be able to view project resources and cannot deploy services or make changes.`,
    action: "Change role",
  };
}

function roleBadgeVariant(role: string) {
  if (role === "owner") return "default";
  if (role === "member") return "secondary";
  return "outline";
}

function statusBadgeVariant(status: string) {
  if (status === "active") return "secondary";
  if (status === "pending") return "outline";
  return "destructive";
}

// Module-level cache — survives tab switches (component unmounts) without lifting
// state to the parent. Keyed by projectId so multi-project navigation is safe.
const membersCache = new Map<string, ProjectMember[]>();

export function MembersTab({ project }: MembersTabProps) {
  const [members, setMembers] = useState<ProjectMember[]>(
    () => membersCache.get(project.id) ?? []
  );
  // loading=true only on true first load (no cache). Background refreshes
  // on tab re-visits leave loading=false so cached rows stay visible.
  const [loading, setLoading] = useState(!membersCache.has(project.id));
  const [inviteOpen, setInviteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  // pendingRemove drives the remove-confirmation dialog. We store the full
  // member object so the dialog can show their name/email and we don't need
  // to re-derive it on confirm.
  const [pendingRemove, setPendingRemove] = useState<ProjectMember | null>(null);
  // pendingRoleChange holds the pending role change until confirmed. On cancel the
  // Select reverts because it is controlled by member.role (unchanged until confirmed).
  const [pendingRoleChange, setPendingRoleChange] = useState<{ member: ProjectMember; newRole: string } | null>(null);

  const isOwner = project.role === "owner";

  const load = useCallback(async () => {
    const isBackground = membersCache.has(project.id);
    if (!isBackground) setLoading(true);
    try {
      const data = await listMembers(project.id);
      membersCache.set(project.id, data);
      setMembers(data);
    } catch (err) {
      handleError(err, { scope: "MembersTab.load", context: { projectId: project.id } });
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Count active owners so we can detect the sole-owner case and protect the
  // role dropdown — demoting the last owner is blocked by the server anyway,
  // but we surface it in the UI too to avoid a confusing 409 error.
  const activeOwnerCount = members.filter(
    (m) => m.role === "owner" && m.status === "active"
  ).length;

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      const updated = await updateMemberRole(project.id, memberId, newRole);
      setMembers((prev) => {
        const next = prev.map((m) => (m.id === memberId ? { ...m, role: updated.role } : m));
        // Keep the cache in sync so the next tab re-visit shows the updated role
        // immediately, before the background refresh has a chance to complete.
        membersCache.set(project.id, next);
        return next;
      });
      notify.success("Role updated");
    } catch (err: any) {
      if (err?.response?.status === 409) {
        notify.error("Cannot demote the last owner.");
      } else {
        handleError(err, { scope: "MembersTab.roleChange" });
      }
    }
  };

  const handleRemove = async (member: ProjectMember) => {
    try {
      await removeMember(project.id, member.id);
      setMembers((prev) => {
        const next = prev.filter((m) => m.id !== member.id);
        // Keep cache in sync — same reason as in handleRoleChange.
        membersCache.set(project.id, next);
        return next;
      });
      notify.success("Member removed", { description: member.email });
    } catch (err: any) {
      if (err?.response?.status === 409) {
        notify.error("Cannot remove the last owner.");
      } else {
        handleError(err, { scope: "MembersTab.remove" });
      }
    } finally {
      setPendingRemove(null);
    }
  };

  const handleResend = async (member: ProjectMember) => {
    try {
      await resendInvite(project.id, member.id);
      notify.success("Invite resent", { description: member.email });
    } catch (err) {
      handleError(err, { scope: "MembersTab.resend" });
    }
  };

  // TODO: Copy invite link — the raw invite token is not exposed on MemberResponse
  // (it is stored server-side only). To implement clipboard copy, the backend
  // would need a separate endpoint that returns the token for a pending invite, or
  // the token would need to be re-generated on demand. For now, owners can use
  // "Resend invite" to send a fresh email instead.

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-h4">Members</h3>
            <p className="text-sm text-muted-foreground">
              Manage who has access to this project.
            </p>
          </div>

          <div className="flex gap-2">
            {/* Non-owners can leave shared projects they were invited to. */}
            {project.role !== "owner" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLeaveOpen(true)}
              >
                Leave project
              </Button>
            )}
            {isOwner && (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="divide-y rounded-lg bg-card" role="status" aria-label="Loading members">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-7 w-24 rounded-md" />
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y rounded-lg bg-card">
            {members.map((member) => {
              // The sole-owner is the current user who is the only active owner.
              // They cannot change their own role — doing so would orphan the project.
              const isSoleOwner =
                member.isCurrentUser &&
                member.role === "owner" &&
                activeOwnerCount === 1;

              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {/* Avatar */}
                  <Avatar className="h-8 w-8">
                    {member.avatarUrl && (
                      <AvatarImage src={member.avatarUrl} alt={member.name || member.email} />
                    )}
                    <AvatarFallback className="text-xs">
                      {initials(member.name, member.email)}
                    </AvatarFallback>
                  </Avatar>

                  {/* Name / email */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {member.name || member.email}
                      {member.isCurrentUser && (
                        <span className="ml-1 text-xs text-muted-foreground">(you)</span>
                      )}
                    </p>
                    {member.name && (
                      <p className="truncate text-xs text-muted-foreground">
                        {member.email}
                      </p>
                    )}
                  </div>

                  {/* Status badge — only shown for non-active members */}
                  {member.status !== "active" && (
                    <Badge variant={statusBadgeVariant(member.status) as any}>
                      {member.status}
                    </Badge>
                  )}

                  {/* Role selector or protected badge.
                      Owners see a dropdown for all rows (including their own) so they
                      can demote themselves when there is another owner. The sole-owner's
                      own row shows a tooltip-wrapped badge instead — the project must
                      always have at least one owner. */}
                  {isOwner && !isSoleOwner ? (
                    <Select
                      value={member.role}
                      onValueChange={(val) => {
                        if (val !== member.role) {
                          setPendingRoleChange({ member, newRole: val });
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 w-28 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : isSoleOwner ? (
                    // Sole-owner protection: show badge + tooltip explaining why the
                    // dropdown is absent. Matches the planning spec (05-frontend.md).
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Badge variant={roleBadgeVariant(member.role) as any}>
                            {member.role}
                          </Badge>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        You are the only owner. Add another owner before changing your role.
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Badge variant={roleBadgeVariant(member.role) as any}>
                      {member.role}
                    </Badge>
                  )}

                  {/* Actions menu — owners can remove any member or resend pending invites.
                      We do not show the menu for the sole-owner's own row (they can't
                      remove themselves either — use "Leave project" which is blocked for
                      sole owners at the API level). */}
                  {isOwner && !isSoleOwner && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {member.status === "pending" && (
                          <>
                            <DropdownMenuItem onClick={() => handleResend(member)}>
                              Resend invite
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {/* Open a confirmation dialog instead of removing immediately —
                            removal is irreversible and accidental clicks should be guarded. */}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setPendingRemove(member)}
                        >
                          {member.status === "pending" ? "Cancel invite" : "Remove member"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <InviteMemberDialog
          projectId={project.id}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          onInvited={(member) => setMembers((prev) => {
            const next = [...prev, member];
            // Keep cache in sync — same reason as in handleRoleChange.
            membersCache.set(project.id, next);
            return next;
          })}
        />

        <LeaveProjectDialog
          projectId={project.id}
          projectName={project.name}
          open={leaveOpen}
          onOpenChange={setLeaveOpen}
        />

        {/* Remove-member confirmation dialog. A single dialog instance is driven
            by pendingRemove so we never nest dialogs inside the member list rows. */}
        <AlertDialog
          open={pendingRemove !== null}
          onOpenChange={(open) => { if (!open) setPendingRemove(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingRemove?.status === "pending"
                  ? "Cancel this invitation?"
                  : `Remove ${pendingRemove?.name || pendingRemove?.email}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingRemove?.status === "pending"
                  ? `The invite sent to ${pendingRemove?.email} will be cancelled and the link will stop working.`
                  : `${pendingRemove?.name || pendingRemove?.email} will immediately lose access to this project.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => {
                  e.preventDefault();
                  if (pendingRemove) handleRemove(pendingRemove);
                }}
              >
                {pendingRemove?.status === "pending" ? "Cancel invite" : "Remove"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Role-change confirmation dialog. A single instance handles all role
            changes — content is derived from pendingRoleChange at render time.
            On cancel the Select reverts because it is controlled by member.role. */}
        {(() => {
          const rc = pendingRoleChange;
          const content = rc ? roleChangeContent(rc.member, rc.newRole) : null;
          return (
            <AlertDialog
              open={!!rc}
              onOpenChange={(open) => { if (!open) setPendingRoleChange(null); }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{content?.title}</AlertDialogTitle>
                  <AlertDialogDescription>{content?.body}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (rc) handleRoleChange(rc.member.id, rc.newRole);
                      setPendingRoleChange(null);
                    }}
                  >
                    {content?.action}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        })()}
      </div>
    </TooltipProvider>
  );
}
